import {
  Admin,
  ConduentSignUp,
  EditBotProfile,
  EditProfileSignup,
  Forgot,
  GdprPolicy,
  GenerateKeys,
  LoginSwitcher,
  MyProfile,
  OAuth,
  Organization,
  Reset,
  Signup,
  Unauthorized,
  UpdatePassword
} from 'components';
import { env } from 'constant/env';
import {
  ROUTE_ADMIN,
  ROUTE_CONDUENT_SIGNUP,
  ROUTE_EDIT_BOT_PROFILE,
  ROUTE_EDIT_PROFILE,
  ROUTE_EDIT_PROFILE_SIGNUP,
  ROUTE_FORGOT,
  ROUTE_GDPR_POLICY_PAGE,
  ROUTE_GENERATE_KEYS,
  ROUTE_LOGIN,
  ROUTE_MY_PROFILE,
  ROUTE_OAUTH,
  ROUTE_ORG,
  ROUTE_PASSWORD_UPDATE_COMPONENT,
  ROUTE_RESET,
  ROUTE_SIGNUP,
  ROUTE_UNAUTHORIZED
} from 'constant/routes';
import { createBrowserHistory as createHistory } from 'history';
import { ThemeProvider } from 'react-jss';
import JssProvider from 'react-jss/lib/JssProvider';
import { Route, Router, Switch } from 'react-router-dom';
import { Toaster } from 'sonner';
import theme from 'style';
import { generateClassName } from 'utils/generateClassName';
import {
  ErrorBoundary,
  GqlClient,
  Notifications,
  PrivateRoute,
  RestClient
} from 'wrappers';
import 'style/style.css';
import '../index.css';

import * as Sentry from '@sentry/react';

import NotFound from './organization/notFound';

export const history = createHistory();

Sentry.init({
  dsn: 'https://8f0ddd225beb5341d3222b85358df338@o366774.ingest.us.sentry.io/4509627849572353',
  tunnel: 'tu-tu-nail',
  environment: env.VITE_ENVIRONMENT,
  debug: env.SENTRY_DEBUG,

  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history
    }),
    Sentry.replayIntegration(),
    Sentry.graphqlClientIntegration({
      endpoints: [/\/graphql/]
    })
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  tracesSampleRate: 1,
  sampleRate: 1.0,
  profilesSampleRate: 0.1,

  enableTracing: true,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['stratumn.com'],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0
});

const Root = () => (
  <ThemeProvider theme={theme}>
    <JssProvider generateClassName={generateClassName}>
      <Router history={history}>
        <ErrorBoundary>
          <RestClient>
            <GqlClient>
              <Notifications>
                <Switch>
                  <Route component={LoginSwitcher} exact path={ROUTE_LOGIN} />
                  <Route component={Forgot} exact path={ROUTE_FORGOT} />
                  <Route component={Reset} exact path={ROUTE_RESET} />
                  <Route component={Signup} exact path={ROUTE_SIGNUP} />
                  <Route
                    component={ConduentSignUp}
                    exact
                    path={ROUTE_CONDUENT_SIGNUP}
                  />
                  <Route component={OAuth} exact path={ROUTE_OAUTH} />
                  <PrivateRoute
                    component={GenerateKeys}
                    exact
                    path={ROUTE_GENERATE_KEYS}
                    withoutHeader
                  />
                  <PrivateRoute
                    component={UpdatePassword}
                    exact
                    path={ROUTE_PASSWORD_UPDATE_COMPONENT}
                    withoutHeader
                  />
                  <PrivateRoute
                    component={EditProfileSignup}
                    exact
                    path={ROUTE_EDIT_PROFILE_SIGNUP}
                    withoutHeader
                  />
                  <PrivateRoute component={Organization} path={ROUTE_ORG} />
                  <PrivateRoute
                    component={({
                      match: {
                        params: { id }
                      }
                    }) => <EditBotProfile botRowId={id} />}
                    path={ROUTE_EDIT_BOT_PROFILE}
                  />
                  <PrivateRoute
                    component={GdprPolicy}
                    exact
                    path={ROUTE_GDPR_POLICY_PAGE}
                  />
                  <Route
                    component={Unauthorized}
                    exact
                    path={ROUTE_UNAUTHORIZED}
                  />
                  <PrivateRoute component={Admin} exact path={ROUTE_ADMIN} />

                  {/*
                      MyProfile owns two exact sub-routes ('/' and
                      '/edit-profile') via its own <Switch>. Matching only those
                      here (instead of a bare non-exact '/') lets unknown URLs
                      fall through to the NotFound catch-all below.
                    */}
                  <PrivateRoute
                    component={MyProfile}
                    exact
                    path={[ROUTE_MY_PROFILE, ROUTE_EDIT_PROFILE]}
                  />
                  <Route component={NotFound} path="*" />
                </Switch>
              </Notifications>
            </GqlClient>
          </RestClient>
        </ErrorBoundary>
        <Toaster closeButton position="bottom-left" richColors />
      </Router>
    </JssProvider>
  </ThemeProvider>
);

export default Root;
